import React, { ChangeEvent, FC, useContext, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { IReviewComment } from '../../shared/interfaces/IReviewComment';
import { AuthContext } from '../../providers/AuthProvider';
import CommentingService from '../../services/CommentingService';
import CommentAiService, {
  IGenerateCommentOptions,
  IReviewMetadata,
} from '../../services/CommentAiService';
import { constants } from '../../constants/constants';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { useTranslation } from 'react-i18next';
import LanguageControl from '../Language/LanguageControl';
import { getReplyMaxCharacters } from '../../shared/helpers/CommunityManagementHelper';

interface Props {
  reviewId: string;
  input: string;
  reviewComment?: IReviewComment;
  communityManagement: CommunityManagement;
  reviewMetadata: IReviewMetadata;
  noReviewText: boolean;
  onSaveSuccess: (comment: IReviewComment) => void;
}

const ReviewReply: FC<Props> = ({
  reviewId,
  input,
  reviewComment,
  communityManagement,
  reviewMetadata,
  noReviewText,
  onSaveSuccess,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [reply, setReply] = useState<IReviewComment>(reviewComment || { text: '', status: 'OPEN' });
  const [deleted, setDeleted] = useState<boolean>(false);
  const [language, setLanguage] = useState<Language | ''>(communityManagement.languagePreference);
  const maxCharacters = getReplyMaxCharacters(communityManagement, reviewMetadata.platform);
  const replyLength = reply.text.length;
  const isReplyOverLimit = maxCharacters !== undefined && replyLength > maxCharacters;

  const handleGenerateReply = async () => {
    const generateCommentOptions: IGenerateCommentOptions = {
      language: language,
      tonality: constants.tonality[communityManagement.tonality.preference],
      duzen: communityManagement.pronoun.preference === 0,
      review: input,
      useCustomerName: communityManagement.useUsername,
      salutation: communityManagement.salutationPrefix,
      signature: communityManagement.greeting,
      additionalPrompt: communityManagement.additionalPrompt,
      maxCharacters,
      reviewMetadata: reviewMetadata,
      noReviewText,
    };
    const data = await CommentAiService.generateComment(generateCommentOptions, setLoading);

    if (!data) return;

    setReply((prevState) => ({
      ...prevState,
      text: data,
    }));
  };

  const handleSaveReply = async () => {
    const data = await CommentingService.saveReviewComment(
      reviewId,
      reply.status,
      reply.text,
      setLoading
    );

    if (!data) return;
    setDeleted(false);
    onSaveSuccess(reply);
  };

  const handleSaveStatus = async (status: string) => {
    const data = await CommentingService.saveReviewComment(
      reviewId,
      status,
      reviewComment?.text ?? '',
      setLoading
    );

    if (!data) return;
    setDeleted(false);
  };

  const handleDeleteReply = async () => {
    const data = await CommentingService.deleteReviewComment(reviewId, setLoading);

    if (!data) return;

    setReply({ text: '', status: '' });
    setDeleted(true);
  };

  return (
    <Box sx={{ mt: theme.spacing(2) }}>
      <TextField
        disabled={loading}
        fullWidth
        label={t('comment')}
        multiline
        rows={8}
        InputLabelProps={{ shrink: !!reply.text }}
        inputProps={{ maxLength: maxCharacters }}
        error={isReplyOverLimit}
        helperText={maxCharacters !== undefined ? `${replyLength} / ${maxCharacters}` : undefined}
        value={reply.text}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setReply((prevState) => ({
            ...prevState,
            text: event.target.value,
          }));
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', my: theme.spacing(2) }}>
        {hasRole('can-use-ai') && (
          <>
            <LanguageControl
              language={language}
              setLanguage={setLanguage}
            />
            <Button
              disabled={loading}
              variant="contained"
              onClick={async () => await handleGenerateReply()}
              sx={{ mr: theme.spacing(1) }}
            >
              {loading ? (
                <CircularProgress
                  sx={{ ml: theme.spacing(1) }}
                  size={24}
                />
              ) : (
                t('generate')
              )}
            </Button>
          </>
        )}
        <Button
          disabled={loading || isReplyOverLimit}
          variant="contained"
          onClick={async () => await handleSaveReply()}
        >
          {loading && !reply.text ? (
            <CircularProgress
              sx={{ ml: theme.spacing(1) }}
              size={24}
            />
          ) : (
            t('save')
          )}
        </Button>
        <Button
          disabled={loading}
          variant="contained"
          color="warning"
          onClick={() => {
            setReply({ text: '', status: '' });
          }}
          sx={{ ml: 1 }}
        >
          {t('reset')}
        </Button>
        <Button
          disabled={loading || !reviewComment?.text || !reviewComment?.status || deleted}
          variant="contained"
          color="error"
          onClick={async () => await handleDeleteReply()}
          sx={{ ml: 1 }}
        >
          {t('delete')}
        </Button>
        <FormControl
          disabled={loading || isReplyOverLimit}
          size="small"
          sx={{ minWidth: '200px', ml: 'auto' }}
        >
          <InputLabel id="status-select-label">{t('status')}</InputLabel>
          <Select
            labelId="status-select-label"
            value={reply.status || 'OPEN'}
            onChange={(event: SelectChangeEvent) => {
              setReply((prevState) => ({
                ...prevState,
                status: event.target.value,
              }));
              handleSaveStatus(event.target.value);
            }}
            label={t('status')}
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            <MenuItem value="OPEN">{t('OPEN')}</MenuItem>
            <MenuItem value="APPROVAL">{t('APPROVAL')}</MenuItem>
            <MenuItem value="DONE">{t('DONE')}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {hasRole('can-use-ai') && (
        <Typography
          sx={{ marginLeft: '0.5rem', marginTop: '0rem' }}
          variant="caption"
        >
          {t('ai_disclaimer_reply')}
        </Typography>
      )}
    </Box>
  );
};

export default ReviewReply;
